import { create } from 'zustand'
import { convex } from '../convexClient'
import { api } from '../lib/convexHelpers'

/**
 * Emoji store - handles emoji reactions in karaoke rooms
 * Manages the emoji events queue and sending emoji reactions
 *
 * Emoji events are synced from Convex reactive queries via useConvexSync.
 * Sending emoji goes directly to Convex mutation.
 */
const useEmojiStore = create((set, get) => ({
  // State
  emojiEvents: [],

  // Actions

  /**
   * Add an emoji event to the queue (called by useConvexSync)
   * Maintains a maximum of 30 events using circular buffer pattern
   */
  addEmojiEvent: (eventPayload) => {
    if (!eventPayload) return

    const normalizedEvent = {
      id: eventPayload.id || `emoji-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
      emoji: typeof eventPayload.emoji === 'string' ? eventPayload.emoji : '👏🏼',
      sentAt: typeof eventPayload.sentAt === 'number' ? eventPayload.sentAt : Date.now()
    }

    set(state => {
      // Prevent duplicates
      if (state.emojiEvents.some(item => item.id === normalizedEvent.id)) {
        return state
      }

      // Efficient circular buffer - slice from end if at capacity
      const nextEvents = state.emojiEvents.length >= 30
        ? [...state.emojiEvents.slice(-29), normalizedEvent]
        : [...state.emojiEvents, normalizedEvent]

      return { emojiEvents: nextEvents }
    })
  },

  /**
   * Send an emoji reaction to the room via Convex mutation
   * Optimistically adds to local state, rolls back on failure
   */
  sendEmojiReaction: async (roomCode, emoji = '👏🏼') => {
    if (!roomCode) {
      throw new Error('Cannot send emoji without joining a room')
    }

    const eventId = `emoji-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`
    const event = {
      id: eventId,
      emoji,
      sentAt: Date.now()
    }

    // Optimistic update
    get().addEmojiEvent(event)

    try {
      await convex.mutation(api.emoji.send, {
        roomCode,
        emoji,
        eventId,
      })
    } catch (error) {
      console.error('Failed to send emoji reaction:', error)

      // Roll back optimistic addition
      set(state => ({
        emojiEvents: state.emojiEvents.filter(item => item.id !== eventId)
      }))
      throw error
    }
  },

  /**
   * Clear all emoji events (used on disconnect)
   */
  clearEmojiEvents: () => {
    set({ emojiEvents: [] })
  }
}))

// Selector hooks for granular subscriptions
export const useEmojiEvents = () => useEmojiStore(state => state.emojiEvents)

export default useEmojiStore
